import { router, usePage } from "@inertiajs/react"
import { useCallback, useEffect, useRef, useState } from "react"

import type {
  TourPage,
  TourSection,
  TourSectionStatus,
  TourStep,
} from "@/components/demo-tour/sections"
import {
  announceDemoTourStep,
  setDemoTourActive,
} from "@/lib/demo-tour-activity"
import { cn, getCsrfToken } from "@/lib/utils"

import "./demo-tour.css"

export interface DemoTourState {
  sections?: Record<string, string>
  active_section?: string | null
  active_step?: number | null
}

export interface TourEngineProps {
  sections: TourSection[]
  /** Server-side progress to resume from (hydrated once on mount). */
  savedState: DemoTourState | null | undefined
  /** PATCH endpoint persisting `{demo_tour: {sections, active_section, active_step}}`.
   *  Omit for a tour with no server persistence — it replays on every visit. */
  persistUrl?: string | null
  /** Navigable URL for a page kind; null when the page can only be reached by
   *  the visitor's own click (detail pages behind dynamic ids). */
  pathForPage: (page: TourPage) => string | null
  pageMatches: (page: TourPage, path: string) => boolean
  /** Pages where the tour auto-opens and starts/resumes on load. */
  isAutoStartPath: (path: string) => boolean
  /** "checklist" (default) shows the sections panel + reopen FAB. "none" runs
   *  the tour as a standalone widget (the agency welcome popup): no panel, no
   *  FAB, and dismissing marks the whole tour seen so it never auto-runs
   *  again. */
  chrome?: "checklist" | "none"
  /** Celebrate when every section finishes. Reserved for the Customer Hub —
   *  the last tour in the demo journey, so it's the one that earns a payoff. */
  celebrateOnFinish?: boolean
}

const SPOT_PADDING = 8

/** Full-screen confetti for the last tour in the demo journey. Fixed values
 *  rather than random so the burst is identical every run and can't churn on
 *  re-render. */
const CONFETTI = Array.from({ length: 90 }, (_, i) => ({
  left: (i * 17) % 100,
  color: ["#F59E0B", "#3B82F6", "#10B981", "#EC4899", "#8B5CF6"][i % 5],
  delay: ((i * 7) % 12) / 10,
  duration: 2.4 + ((i * 13) % 12) / 10,
}))
const STATUSES: TourSectionStatus[] = ["pending", "active", "done", "skipped"]

function isStatus(value: string | undefined): value is TourSectionStatus {
  return STATUSES.includes(value as TourSectionStatus)
}

/** The demo checklist + spotlight tour engine (visuals from the approved
 *  mockup). Mount it only while its tour is enabled for the current page's
 *  props; progress persists server-side so reloads, cross-tab hand-offs, and
 *  remounts resume where the demo left off. */
export default function TourEngine({
  sections: SECTIONS,
  savedState,
  persistUrl,
  pathForPage,
  pageMatches,
  isAutoStartPath,
  chrome = "checklist",
  celebrateOnFinish = false,
}: TourEngineProps) {
  const showChecklist = chrome === "checklist"
  const page = usePage()
  const currentPath = page.url.split("?")[0].replace(/\/$/, "")

  const [statuses, setStatuses] = useState<TourSectionStatus[]>(() =>
    SECTIONS.map(() => "pending"),
  )
  const [pos, setPos] = useState<{ sec: number; step: number } | null>(null)
  const [panelOpen, setPanelOpen] = useState(false)

  // Hold ambient overlays back while a tour is on screen, and release them
  // when it closes or the engine unmounts.
  useEffect(() => {
    setDemoTourActive(pos !== null)
  }, [pos])
  useEffect(() => () => setDemoTourActive(false), [])
  const [tipShown, setTipShown] = useState(false)
  // Where the user dragged the panel (viewport px); null = the CSS default
  // (lower-left). Survives minimize/reopen via the style prop on re-mount.
  const [panelDragPos, setPanelDragPos] = useState<{
    x: number
    y: number
  } | null>(null)
  const [dragging, setDragging] = useState(false)

  const targetRef = useRef<HTMLElement | null>(null)
  const shadeRefs = useRef<(HTMLDivElement | null)[]>([])
  const spotRef = useRef<HTMLDivElement | null>(null)
  const tipRef = useRef<HTMLDivElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const fabRef = useRef<HTMLButtonElement | null>(null)
  const hydratedRef = useRef(false)
  const autoStartedRef = useRef(false)
  const pausedPosRef = useRef<{ sec: number; step: number } | null>(null)
  const lastNavKeyRef = useRef<string | null>(null)
  const suppressNavUntilRef = useRef(0)
  const lastRectRef = useRef<DOMRect | null>(null)
  const lastPanelRectRef = useRef<DOMRect | null>(null)
  const floatingRef = useRef(false)
  // The current step asks for its tooltip above the target (TourStep.placement).
  const placeAboveRef = useRef(false)
  const placeSideRef = useRef(false)
  // Which layout ran last: true = floating, false = spotlight, null = neither
  // yet. Switching between the two layouts must snap rather than animate, since
  // their shade geometries are unrelated; the null start keeps the very first
  // step free to fade in normally.
  const wasFloatingRef = useRef<boolean | null>(null)

  const step: TourStep | null = pos ? SECTIONS[pos.sec].steps[pos.step] : null

  // Standalone mode: nothing is written until the user actually clicks a
  // button, and the first click flags the whole tour as seen.
  const userActedRef = useRef(false)

  // ---- persistence ----------------------------------------------------------

  const persist = useCallback(
    (
      nextStatuses: TourSectionStatus[],
      nextPos: { sec: number; step: number } | null,
    ) => {
      // Standalone (chrome="none") persistence is one-way: the moment the user
      // interacts (Next or Skip), every section is stored terminal — done if
      // finished, skipped otherwise — and no resume position is ever saved.
      // Displaying the widget alone writes nothing, and no later save can put
      // it back into a resumable state, so it can never reappear on reload.
      if (!persistUrl) return

      let statusesToStore = nextStatuses
      let posToStore = nextPos
      if (chrome === "none") {
        if (!userActedRef.current) return
        statusesToStore = nextStatuses.map((status) =>
          status === "done" ? "done" : "skipped",
        )
        posToStore = null
      }

      const sectionsMap: Record<string, string> = {}
      SECTIONS.forEach((section, i) => {
        sectionsMap[section.id] = statusesToStore[i]
      })
      fetch(persistUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        body: JSON.stringify({
          demo_tour: {
            sections: sectionsMap,
            active_section: posToStore ? SECTIONS[posToStore.sec].id : null,
            active_step: posToStore ? posToStore.step : null,
          },
        }),
      }).catch(() => undefined)
    },
    [SECTIONS, persistUrl],
  )

  // Hydrate once from the server-side state so a reload (or a hand-off into a
  // new tab) resumes instead of restarting.
  useEffect(() => {
    if (hydratedRef.current) return
    hydratedRef.current = true

    const saved = savedState ?? {}
    const savedSections = saved.sections ?? {}
    const next = SECTIONS.map<TourSectionStatus>((section) => {
      const value = savedSections[section.id]
      if (!isStatus(value)) return "pending"
      // "active" is a live-session state; a fresh load resumes it explicitly
      // via the saved position below.
      return value === "active" ? "pending" : value
    })

    const savedSecIdx = SECTIONS.findIndex(
      (section) => section.id === saved.active_section,
    )
    if (savedSecIdx >= 0) {
      const stepCount = SECTIONS[savedSecIdx].steps.length
      const savedStep = Math.min(
        Math.max(saved.active_step ?? 0, 0),
        stepCount - 1,
      )
      next[savedSecIdx] = "active"
      pausedPosRef.current = { sec: savedSecIdx, step: savedStep }
    }
    setStatuses(next)
  }, [SECTIONS, savedState])

  // ---- section/step flow ----------------------------------------------------

  const startSection = useCallback(
    (sec: number, atStep = 0) => {
      setStatuses((prev) => {
        const next = [...prev]
        prev.forEach((status, i) => {
          if (status === "active" && i !== sec) next[i] = "pending"
        })
        next[sec] = "active"
        persist(next, { sec, step: atStep })
        return next
      })
      pausedPosRef.current = null
      setPos({ sec, step: atStep })
      setPanelOpen(true)
    },
    [persist],
  )

  const stopOverlays = useCallback(() => {
    setTipShown(false)
    targetRef.current = null
    lastRectRef.current = null
  }, [])

  const pauseTour = useCallback(() => {
    if (pos) pausedPosRef.current = pos
    stopOverlays()
    setPos(null)
  }, [pos, stopOverlays])

  // Standalone (chrome="none") close: the whole tour counts as seen — every
  // unfinished section becomes "skipped" and is persisted, so it never
  // auto-runs for this user again.
  const dismissTour = useCallback(() => {
    userActedRef.current = true
    setStatuses((prev) => {
      const next = prev.map<TourSectionStatus>((status) =>
        status === "done" ? "done" : "skipped",
      )
      persist(next, null)
      return next
    })
    pausedPosRef.current = null
    setPos(null)
    stopOverlays()
  }, [persist, stopOverlays])

  const finishSection = useCallback(
    (how: "done" | "skipped") => {
      if (!pos) return
      userActedRef.current = true
      const sec = pos.sec
      setStatuses((prev) => {
        const next = [...prev]
        next[sec] = how
        // Continue forward only; never jump back to earlier parts.
        let forward = -1
        for (let i = sec + 1; i < next.length; i++) {
          if (next[i] === "pending") {
            forward = i
            break
          }
        }
        if (forward !== -1) {
          next[forward] = "active"
          persist(next, { sec: forward, step: 0 })
          setPos({ sec: forward, step: 0 })
        } else {
          persist(next, null)
          setPos(null)
          stopOverlays()
        }
        return next
      })
    },
    [pos, persist, stopOverlays],
  )

  const advanceStep = useCallback(() => {
    if (!pos) return
    userActedRef.current = true
    const section = SECTIONS[pos.sec]
    if (pos.step < section.steps.length - 1) {
      const next = { sec: pos.sec, step: pos.step + 1 }
      setPos(next)
      persist(statuses, next)
    } else {
      finishSection("done")
    }
  }, [pos, statuses, persist, finishSection, SECTIONS])

  const backStep = useCallback(() => {
    if (!pos) return
    if (pos.step > 0) {
      setPos({ sec: pos.sec, step: pos.step - 1 })
    } else if (pos.sec > 0) {
      const prevSec = pos.sec - 1
      setStatuses((prev) => {
        const next = [...prev]
        next[pos.sec] = "pending"
        next[prevSec] = "active"
        return next
      })
      setPos({ sec: prevSec, step: SECTIONS[prevSec].steps.length - 1 })
    }
  }, [pos, SECTIONS])

  const restartTour = useCallback(() => {
    const next = SECTIONS.map<TourSectionStatus>(() => "pending")
    next[0] = "active"
    setStatuses(next)
    persist(next, { sec: 0, step: 0 })
    pausedPosRef.current = null
    setPos({ sec: 0, step: 0 })
  }, [persist, SECTIONS])

  const openSection = useCallback(
    (sec: number) => {
      const paused = pausedPosRef.current
      if (paused && paused.sec === sec) {
        startSection(sec, paused.step)
      } else {
        startSection(sec)
      }
    },
    [startSection],
  )

  // Drag the panel by its header, clamped so it can't be lost off-screen.
  const onPanelDragStart = useCallback((e: React.PointerEvent) => {
    // The minimize button lives in the header — clicks on it aren't drags.
    if ((e.target as HTMLElement).closest("button")) return
    const panel = panelRef.current
    if (!panel) return
    e.preventDefault()

    const rect = panel.getBoundingClientRect()
    const grabX = e.clientX - rect.left
    const grabY = e.clientY - rect.top
    setDragging(true)

    const clamp = (clientX: number, clientY: number) => ({
      x: Math.min(
        Math.max(8, clientX - grabX),
        window.innerWidth - rect.width - 8,
      ),
      y: Math.min(
        Math.max(8, clientY - grabY),
        window.innerHeight - rect.height - 8,
      ),
    })

    const onMove = (ev: PointerEvent) => {
      const next = clamp(ev.clientX, ev.clientY)
      // Imperative during the drag (no re-render per frame)…
      Object.assign(panel.style, {
        left: `${next.x}px`,
        top: `${next.y}px`,
        bottom: "auto",
      })
    }
    const onUp = (ev: PointerEvent) => {
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", onUp)
      window.removeEventListener("pointercancel", onUp)
      setDragging(false)
      // …then committed as state so the position survives minimize/reopen.
      setPanelDragPos(clamp(ev.clientX, ev.clientY))
    }
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onUp)
    window.addEventListener("pointercancel", onUp)
  }, [])

  // Auto-start on designated pages, resuming the saved position when one
  // exists (mirrors the mockup's on-load start, minus the restart-on-reload).
  // The started flag is set when the timer FIRES, not when it's scheduled —
  // the hydration setStatuses re-runs this effect, and its cleanup cancels the
  // pending timer; marking early meant the reschedule was skipped and
  // auto-start never fired.
  useEffect(() => {
    if (autoStartedRef.current || !isAutoStartPath(currentPath)) return

    const timer = window.setTimeout(() => {
      autoStartedRef.current = true
      setPanelOpen(true)
      const paused = pausedPosRef.current
      if (paused) {
        startSection(paused.sec, paused.step)
        return
      }
      const firstPending = statuses.findIndex((status) => status === "pending")
      if (firstPending !== -1) startSection(firstPending)
    }, 700)
    return () => window.clearTimeout(timer)
  }, [currentPath, isAutoStartPath, startSection, statuses])

  // ---- placement ------------------------------------------------------------

  useEffect(() => {
    if (!pos || !step) return

    if (!pageMatches(step.page, currentPath)) {
      // Off the step's page: drop the previous step's spotlight so it can't
      // sit over this page with the new step's copy.
      setTipShown(false)
      targetRef.current = null
      const requiredPath = pathForPage(step.page)?.replace(/\/$/, "")
      if (!requiredPath) return
      // A "click it" step navigates via the visitor's own click — don't fight
      // it with a programmatic visit while that navigation is in flight. The
      // nav key also makes the visit one-shot: for detail pages the engine
      // navigates to the nearest list page and waits for the visitor to click
      // through.
      const navKey = `${pos.sec}:${pos.step}:${requiredPath}`
      if (
        Date.now() > suppressNavUntilRef.current &&
        lastNavKeyRef.current !== navKey
      ) {
        lastNavKeyRef.current = navKey
        router.visit(requiredPath)
      }
      return
    }

    let cancelled = false
    let attempts = 0
    setTipShown(false)
    targetRef.current = null
    floatingRef.current = Boolean(step.floating)
    placeAboveRef.current = step.placement === "above"
    placeSideRef.current = step.placement === "side"

    // Floating steps have no anchor — show the centered card straight away.
    if (step.floating) {
      const timer = window.setTimeout(() => {
        if (!cancelled) {
          lastRectRef.current = null
          setTipShown(true)
        }
      }, 250)
      return () => {
        cancelled = true
        window.clearTimeout(timer)
      }
    }

    const tryPlace = () => {
      if (cancelled) return
      const el = document.querySelector<HTMLElement>(
        `[data-tour="${step.target}"]`,
      )
      if (!el) {
        attempts += 1
        if (attempts < 20) {
          window.setTimeout(tryPlace, 150)
          return
        }
        if (step.optional) {
          // The anchor legitimately doesn't exist for this demo (e.g. Talk to
          // a Human unconfigured) — the PF flows branched around these.
          advanceStep()
          return
        }
        // Anchor never appeared (data variant hid the block) — degrade to
        // the page body rather than stranding the tour.
        const fallback = document.querySelector<HTMLElement>("main")
        if (fallback) place(fallback)
        return
      }
      place(el)
    }

    const place = (el: HTMLElement) => {
      targetRef.current = el
      if (step.target) announceDemoTourStep(step.target)
      el.scrollIntoView({ behavior: "smooth", block: "center" })
      window.setTimeout(() => {
        if (!cancelled) {
          lastRectRef.current = null
          setTipShown(true)
        }
      }, 480)
    }

    const initialDelay = window.setTimeout(tryPlace, 60)
    return () => {
      cancelled = true
      window.clearTimeout(initialDelay)
    }
  }, [pos, step, currentPath, pathForPage, pageMatches, advanceStep])

  // Spotlight + tooltip follow the target through scrolls, resizes, and layout
  // shifts: a per-frame watchdog re-measures the rect and repositions
  // imperatively (no React re-render per frame).
  useEffect(() => {
    if (!tipShown) {
      shadeRefs.current.forEach((el) => el?.classList.remove("on"))
      spotRef.current?.classList.remove("on")
      tipRef.current?.classList.remove("on", "floating")
      return
    }

    let raf = 0
    let restoreTimer = 0

    const layout = () => {
      const target = targetRef.current
      const tip = tipRef.current
      const spot = spotRef.current
      if (!tip || !spot) return

      // Floating (welcome-popup) steps: centered modal card over a
      // full-viewport dim — same darkening as the spotlight steps, minus the
      // spotlight.
      if (floatingRef.current) {
        const [t, b, l, rt] = shadeRefs.current
        // Arriving from a spotlight step: snap out of the cutout geometry
        // instead of letting the four shades animate into the full-viewport
        // dim, which reads as the darkness growing unevenly across the page.
        if (wasFloatingRef.current === false) {
          const pieces = [
            ...shadeRefs.current.filter(Boolean),
            spot,
            tip,
          ] as HTMLElement[]
          pieces.forEach((el) => el.classList.add("no-trans"))
          window.clearTimeout(restoreTimer)
          restoreTimer = window.setTimeout(() => {
            pieces.forEach((el) => el.classList.remove("no-trans"))
          }, 180)
        }
        if (t)
          Object.assign(t.style, {
            left: "0px",
            top: "0px",
            width: `${window.innerWidth}px`,
            height: `${window.innerHeight}px`,
          })
        ;[b, l, rt].forEach(
          (el) =>
            el && Object.assign(el.style, { width: "0px", height: "0px" }),
        )
        shadeRefs.current.forEach((el) => el?.classList.add("on"))
        spot.classList.remove("on")
        tip.classList.add("floating")
        tip.style.left = `${Math.max(12, (window.innerWidth - tip.offsetWidth) / 2)}px`
        tip.style.top = `${Math.max(24, (window.innerHeight - tip.offsetHeight) * 0.42)}px`
        tip.classList.add("on")
        wasFloatingRef.current = true
        return
      }
      tip.classList.remove("floating")
      const cameFromFloating = wasFloatingRef.current === true

      if (!target?.isConnected) return

      const r = target.getBoundingClientRect()
      const panelRect = panelOpen
        ? (panelRef.current?.getBoundingClientRect() ?? null)
        : null
      const last = lastRectRef.current
      const lastPanel = lastPanelRectRef.current
      const rectsEqual = (a: DOMRect | null, b: DOMRect | null) =>
        a === b ||
        (a !== null &&
          b !== null &&
          a.top === b.top &&
          a.left === b.left &&
          a.width === b.width &&
          a.height === b.height)
      // Re-layout when either the target moved or the panel was dragged (the
      // tooltip dodges the panel, so its position depends on both).
      if (
        last &&
        !cameFromFloating &&
        rectsEqual(r, last) &&
        rectsEqual(panelRect, lastPanel)
      ) {
        return
      }
      wasFloatingRef.current = false
      const moved = last !== null || cameFromFloating
      lastRectRef.current = r
      lastPanelRectRef.current = panelRect

      const pieces = [
        ...shadeRefs.current.filter(Boolean),
        spot,
        tip,
      ] as HTMLElement[]
      if (moved) {
        pieces.forEach((el) => el.classList.add("no-trans"))
        window.clearTimeout(restoreTimer)
        restoreTimer = window.setTimeout(() => {
          pieces.forEach((el) => el.classList.remove("no-trans"))
        }, 180)
      }

      const x = r.left - SPOT_PADDING
      const y = r.top - SPOT_PADDING
      const w = r.width + SPOT_PADDING * 2
      const h = r.height + SPOT_PADDING * 2
      const W = window.innerWidth
      const H = window.innerHeight
      const [t, b, l, rt] = shadeRefs.current
      if (t)
        Object.assign(t.style, {
          left: "0px",
          top: "0px",
          width: `${W}px`,
          height: `${Math.max(0, y)}px`,
        })
      if (b)
        Object.assign(b.style, {
          left: "0px",
          top: `${y + h}px`,
          width: `${W}px`,
          height: `${Math.max(0, H - (y + h))}px`,
        })
      if (l)
        Object.assign(l.style, {
          left: "0px",
          top: `${y}px`,
          width: `${Math.max(0, x)}px`,
          height: `${h}px`,
        })
      if (rt)
        Object.assign(rt.style, {
          left: `${x + w}px`,
          top: `${y}px`,
          width: `${Math.max(0, W - (x + w))}px`,
          height: `${h}px`,
        })
      Object.assign(spot.style, {
        left: `${x}px`,
        top: `${y}px`,
        width: `${w}px`,
        height: `${h}px`,
      })

      const th = tip.offsetHeight
      const tw = tip.offsetWidth
      // Below the target by default, flipping above when it doesn't fit. A
      // step whose copy points at what sits under the target asks for above
      // first, so the tooltip doesn't cover it.
      const below = r.bottom + 14
      const above = r.top - th - 14
      let top: number
      if (placeAboveRef.current && above >= 10) top = above
      else top = below + th > H - 10 ? above : below
      let left = Math.min(Math.max(12, r.left), W - tw - 12)
      const cramped = placeSideRef.current || top < 10
      if (cramped) {
        // Neither above nor below fits (a tall target such as the pre-plan
        // questions form), or the step asked to sit beside its target. Pick
        // the spot that covers the least of the target, and of the checklist
        // panel, so "click it" steps stay clickable.
        const clampLeft = (v: number) => Math.min(Math.max(12, v), W - tw - 12)
        const clampTop = (v: number) => Math.min(Math.max(10, v), H - th - 10)
        const midTop = clampTop(y + (h - th) / 2)
        const sides = [
          { left: clampLeft(x + w + 14), top: midTop },
          { left: clampLeft(x - tw - 14), top: midTop },
        ]
        const edges = [
          { left: clampLeft(r.left), top: 10 },
          { left: clampLeft(r.left), top: H - th - 10 },
        ]
        const overlap = (
          c: { left: number; top: number },
          o: { left: number; top: number; right: number; bottom: number },
        ) =>
          Math.max(
            0,
            Math.min(c.left + tw, o.right) - Math.max(c.left, o.left),
          ) *
          Math.max(0, Math.min(c.top + th, o.bottom) - Math.max(c.top, o.top))
        const targetBox = { left: x, top: y, right: x + w, bottom: y + h }
        const cost = (c: { left: number; top: number }) =>
          overlap(c, targetBox) + (panelRect ? overlap(c, panelRect) : 0)
        const pick = (cs: { left: number; top: number }[]) =>
          cs.reduce((a, c) => (cost(c) < cost(a) ? c : a))
        // A "side" step keeps the text around its target readable, unless the
        // viewport is too narrow and a side spot would cover the target.
        const bestSide = pick(sides)
        const best =
          placeSideRef.current && overlap(bestSide, targetBox) <= 0.15 * tw * th
            ? bestSide
            : pick([...sides, ...edges])
        left = best.left
        top = best.top
      }
      // Keep the tooltip clear of the checklist panel wherever the user has
      // dragged it: when they'd overlap, slide the tooltip beside the panel.
      if (panelRect && !cramped) {
        const overlapsH =
          left < panelRect.right + 12 && left + tw > panelRect.left - 12
        const overlapsV =
          top < panelRect.bottom + 12 && top + th > panelRect.top - 12
        if (overlapsH && overlapsV) {
          const rightOf = panelRect.right + 12
          const leftOf = panelRect.left - tw - 12
          left = rightOf + tw <= W - 12 ? rightOf : Math.max(12, leftOf)
        }
      }
      tip.style.top = `${top}px`
      tip.style.left = `${left}px`

      shadeRefs.current.forEach((el) => el?.classList.add("on"))
      spot.classList.add("on")
      tip.classList.add("on")
    }

    const watchdog = () => {
      layout()
      raf = requestAnimationFrame(watchdog)
    }
    raf = requestAnimationFrame(watchdog)
    return () => {
      cancelAnimationFrame(raf)
      window.clearTimeout(restoreTimer)
    }
  }, [tipShown, panelOpen])

  // Interaction guard: while a step is showing, clicks land only inside the
  // tooltip, the checklist, the FAB — or the highlighted element itself on
  // "click it" steps, where that click advances the tour.
  useEffect(() => {
    if (!pos || !step) return

    const guard = (e: MouseEvent) => {
      const node = e.target as Node
      if (
        tipRef.current?.contains(node) ||
        panelRef.current?.contains(node) ||
        fabRef.current?.contains(node)
      ) {
        return
      }
      const target = targetRef.current
      if (
        step.action === "click" &&
        target &&
        (target === node || target.contains(node))
      ) {
        if (step.advanceOn) {
          const trigger = target.querySelector(
            `[data-tour="${step.advanceOn}"]`,
          )
          if (!trigger || !(trigger === node || trigger.contains(node))) return
        }
        suppressNavUntilRef.current = Date.now() + 2000
        window.setTimeout(() => advanceStep(), 450)
        return
      }
      e.preventDefault()
      e.stopPropagation()
    }

    document.addEventListener("click", guard, true)
    return () => document.removeEventListener("click", guard, true)
  }, [pos, step, advanceStep])

  // ---- render ---------------------------------------------------------------

  const total = SECTIONS.length
  const doneCount = statuses.filter(
    (status) => status === "done" || status === "skipped",
  ).length
  const allDone = doneCount === total

  // Celebrate once, and only on a real false → true transition. Starts as null
  // so hydrating an already-finished tour (a reload after the demo) records the
  // state without replaying the confetti.
  const [celebrating, setCelebrating] = useState(false)
  const wasAllDoneRef = useRef<boolean | null>(null)
  useEffect(() => {
    const previously = wasAllDoneRef.current
    wasAllDoneRef.current = allDone
    if (!celebrateOnFinish || previously !== false || !allDone) return
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return

    setCelebrating(true)
    const timer = window.setTimeout(() => setCelebrating(false), 4200)
    return () => window.clearTimeout(timer)
  }, [allDone, celebrateOnFinish])

  const section = pos ? SECTIONS[pos.sec] : null
  const lastStep = pos !== null && pos.step === (section?.steps.length ?? 0) - 1
  const lastSection = pos !== null && pos.sec === total - 1
  const isAction = step?.action === "click"
  const nextLabel =
    step?.btn ??
    (lastStep ? (lastSection ? "Finish tour ✓" : "Finish part ✓") : "Next →")

  return (
    <>
      {celebrating && (
        <div className="demo-tour-confetti" aria-hidden>
          {CONFETTI.map((c, i) => (
            <i
              key={i}
              style={{
                left: `${c.left}%`,
                background: c.color,
                animationDelay: `${c.delay}s`,
                animationDuration: `${c.duration}s`,
              }}
            />
          ))}
        </div>
      )}
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="demo-tour-shade"
          ref={(el) => {
            shadeRefs.current[i] = el
          }}
        />
      ))}
      <div className="demo-tour-spot" ref={spotRef} />

      <div className="demo-tour-tip" ref={tipRef}>
        {pos && step && section && (
          <>
            {showChecklist && (
              <button
                className="x"
                title="Pause tour"
                onClick={pauseTour}
                type="button"
              >
                ✕
              </button>
            )}
            {showChecklist && (
              <span className="part">
                Part {pos.sec + 1} · {section.label}
              </span>
            )}
            <h4>{step.title}</h4>
            <div className="body">
              {typeof step.body === "function"
                ? step.body(page.props as Record<string, unknown>)
                : step.body}
            </div>
            {step.note && <div className="note">{step.note}</div>}
            {/* On its own line: beside the buttons it squeezed the count and
                "Skip part" into two lines each. */}
            {isAction && (
              <span className="hint">
                👆 {step.hint ?? "Click the highlighted element"}
              </span>
            )}
            <div className="nav">
              <span className="cnt">
                {pos.step + 1} of {section.steps.length}
              </span>
              {(pos.sec > 0 || pos.step > 0) && (
                <button title="Back" onClick={backStep} type="button">
                  ←
                </button>
              )}
              {!(lastSection && lastStep) && (
                <button
                  onClick={
                    showChecklist ? () => finishSection("skipped") : dismissTour
                  }
                  type="button"
                >
                  {showChecklist ? "Skip part" : "Skip"}
                </button>
              )}
              {!isAction && (
                <button className="next" onClick={advanceStep} type="button">
                  {nextLabel}
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {!showChecklist ? null : panelOpen ? (
        <div
          className="demo-tour-panel"
          ref={panelRef}
          style={
            panelDragPos
              ? {
                  left: panelDragPos.x,
                  top: panelDragPos.y,
                  bottom: "auto",
                }
              : undefined
          }
        >
          <div
            className={cn("ph", dragging && "dragging")}
            onPointerDown={onPanelDragStart}
          >
            <span className="pt">Demo tour</span>
            <span className={cn("pc", allDone && "done")}>
              {doneCount}/{total}
            </span>
            <button
              className="pmin"
              title="Minimize"
              type="button"
              onClick={() => {
                pauseTour()
                setPanelOpen(false)
              }}
            >
              –
            </button>
          </div>
          <div className="pl">
            {SECTIONS.map((s, i) => {
              const status = statuses[i]
              const isActive = pos?.sec === i
              return (
                <button
                  key={s.id}
                  type="button"
                  className={cn("pi", status, isActive && "active")}
                  onClick={() => openSection(i)}
                >
                  <span className={cn("pic", status)}>
                    {status === "done"
                      ? "✓"
                      : status === "skipped"
                        ? "–"
                        : i + 1}
                  </span>
                  <span className="pil">{s.label}</span>
                  {isActive && pos && (
                    <span className="pp">
                      {pos.step + 1}/{s.steps.length}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
          <div className={cn("pf", allDone && "done")}>
            {allDone && <span>🎉 Tour complete</span>}
            <button onClick={restartTour} type="button">
              ↺ Restart
            </button>
          </div>
        </div>
      ) : (
        <button
          className="demo-tour-fab"
          ref={fabRef}
          type="button"
          onClick={() => setPanelOpen(true)}
        >
          ▶ Demo tour
        </button>
      )}
    </>
  )
}
